/**
 * OnboardingChecklist — Widget d'activation V4 (sticky bottom-right).
 *
 * Se replie/se déplie, disparaît automatiquement quand `completed=true`
 * (après célébration confetti + dismiss automatique) ou quand l'utilisateur
 * clique "Ne plus afficher". Persistant côté backend via
 * `POST /onboarding/checklist/dismiss`.
 *
 * Micro-délices :
 *  - Shimmer sur la progress bar quand une étape se coche
 *  - Confetti burst quand la 4/4 est atteinte
 *  - Banner "🎉 Prêt à facturer !" avec bouton "Terminer"
 *
 * Ne s'affiche PAS pour :
 *  - staff (impersonation)
 *  - utilisateurs sans permission `invoices.write` (visitor / cfo / hr)
 *  - tenants qui ont déjà tout coché et cliqué Terminer OU dismissé
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Circle, ChevronDown, ChevronUp, X, Rocket, Sparkles, Loader2, PartyPopper } from "lucide-react";
import confetti from "canvas-confetti";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { APP_NAME } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const HIDDEN_ROUTES = ["/login", "/register", "/staff", "/accept-invite", "/app/legal"];
const COLLAPSED_KEY = "clarix.onboarding_checklist.collapsed";
const CELEBRATED_KEY = "clarix.onboarding_checklist.celebrated_tenants";

function fireConfetti() {
  const opts = {
    origin: { x: 0.92, y: 0.9 },
    spread: 70,
    startVelocity: 45,
    ticks: 200,
    zIndex: 10000,
    colors: ["#8b5cf6", "#d946ef", "#ec4899", "#fbbf24", "#34d399"],
  };
  confetti({ ...opts, particleCount: 90, angle: 120 });
  setTimeout(() => confetti({ ...opts, particleCount: 60, angle: 100, spread: 100 }), 180);
  setTimeout(() => confetti({ ...opts, particleCount: 40, angle: 140, spread: 55 }), 360);
}

export default function OnboardingChecklist() {
  const { session, hasPermission } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSED_KEY) === "1",
  );
  const [shimmer, setShimmer] = useState(false);
  const [celebrated, setCelebrated] = useState(false);
  const prevDoneRef = useRef(null);
  const prevTenantRef = useRef(null);

  const canSee = !!session
    && !session.staff
    && hasPermission?.("invoices.write");

  const reload = useCallback(async () => {
    if (!canSee) { setState(null); return; }
    setLoading(true);
    try {
      const r = await api.get("/onboarding/checklist");
      setState(r.data);
    } catch { /* silencieux : feature invoicing peut être off */ }
    finally { setLoading(false); }
  }, [canSee]);

  useEffect(() => { reload(); }, [reload, session?.tenant?.id]);

  // Recharger à chaque changement de route pour refléter les progrès
  useEffect(() => {
    if (canSee && state) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname]);

  // Reset celebration state on tenant switch
  useEffect(() => {
    const tid = session?.tenant?.id;
    if (tid && prevTenantRef.current !== tid) {
      prevTenantRef.current = tid;
      prevDoneRef.current = null;
      setCelebrated(false);
    }
  }, [session?.tenant?.id]);

  // Micro-délices : shimmer + confetti quand done_count augmente
  useEffect(() => {
    if (!state) return;
    const prev = prevDoneRef.current;
    const now = state.done_count;
    if (prev !== null && now > prev) {
      // Shimmer
      setShimmer(true);
      setTimeout(() => setShimmer(false), 1200);
    }
    // Confetti quand on atteint 100% pour la 1re fois (par tenant)
    const tid = session?.tenant?.id;
    if (state.completed && tid) {
      let celebratedTenants = [];
      try {
        celebratedTenants = JSON.parse(localStorage.getItem(CELEBRATED_KEY) || "[]");
      } catch { celebratedTenants = []; }
      if (!celebratedTenants.includes(tid)) {
        fireConfetti();
        celebratedTenants.push(tid);
        localStorage.setItem(CELEBRATED_KEY, JSON.stringify(celebratedTenants));
      }
      setCelebrated(true);
    }
    prevDoneRef.current = now;
  }, [state, session?.tenant?.id]);

  const dismiss = async () => {
    setLoading(true);
    try {
      await api.post("/onboarding/checklist/dismiss", {});
      toast.success("Checklist masquée. Tu peux la ré-ouvrir dans Paramètres.");
      setState((s) => s ? { ...s, dismissed: true, should_show: false } : s);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const finishCelebration = async () => {
    // Confetti final + dismiss automatique
    fireConfetti();
    setTimeout(async () => {
      try {
        await api.post("/onboarding/checklist/dismiss", {});
        setState((s) => s ? { ...s, dismissed: true, should_show: false } : s);
      } catch { /* silent */ }
    }, 400);
  };

  const toggleCollapse = () => {
    setCollapsed((v) => {
      const next = !v;
      localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0");
      return next;
    });
  };

  const routeHidden = HIDDEN_ROUTES.some((p) => loc.pathname.startsWith(p));

  // Cas 1 : rien à afficher (pas de session, route bloquée, tenant validé, dismissed)
  if (!canSee || routeHidden || !state) return null;
  const showCelebration = state.completed && !state.dismissed && celebrated;
  if (!state.should_show && !showCelebration) return null;

  const { steps, done_count, total, progress, completed } = state;
  const nextStep = steps.find((s) => !s.done);

  return (
    <AnimatePresence>
      <motion.div
        key="onboarding-checklist"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 24 }}
        transition={{ type: "spring", damping: 22 }}
        className="fixed bottom-4 right-4 z-40 w-80 sm:w-96"
        data-testid="onboarding-checklist-widget">
        <div className={cn(
          "rounded-xl border bg-white shadow-xl overflow-hidden transition",
          completed ? "border-emerald-300 shadow-emerald-200/50" : "border-slate-200",
        )}>
          {/* Header */}
          <button
            type="button"
            onClick={toggleCollapse}
            className={cn(
              "w-full flex items-center justify-between gap-2 px-4 py-3 text-white transition",
              completed
                ? "bg-gradient-to-r from-emerald-500 to-teal-600"
                : "bg-gradient-to-r from-violet-600 to-fuchsia-600",
            )}
            data-testid="onboarding-checklist-toggle">
            <div className="flex items-center gap-2 min-w-0">
              {completed
                ? <PartyPopper className="w-4 h-4 flex-shrink-0" />
                : <Rocket className="w-4 h-4 flex-shrink-0" />}
              <div className="text-left min-w-0">
                <div className="text-sm font-semibold truncate"
                     style={{ fontFamily: "Manrope, sans-serif" }}>
                  {completed ? "Prêt à facturer !" : `Bien démarrer sur ${APP_NAME}`}
                </div>
                <div className="text-[11px] text-white/85">
                  {done_count}/{total} étapes • {progress}%
                </div>
              </div>
            </div>
            {collapsed
              ? <ChevronUp className="w-4 h-4 flex-shrink-0" />
              : <ChevronDown className="w-4 h-4 flex-shrink-0" />}
          </button>

          {/* Progress bar avec shimmer */}
          <div className="h-1.5 bg-slate-100 relative overflow-hidden">
            <div
              className={cn(
                "h-full transition-all duration-700 relative",
                completed
                  ? "bg-gradient-to-r from-emerald-400 to-teal-500"
                  : "bg-gradient-to-r from-violet-500 to-fuchsia-500",
              )}
              style={{ width: `${progress}%` }}>
              {shimmer && (
                <span
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.85) 50%, transparent 100%)",
                    animation: "onboarding-shimmer 1.1s ease-out",
                  }}
                />
              )}
            </div>
          </div>

          {/* Body */}
          {!collapsed && (
            <div className="p-4 space-y-2">
              {showCelebration ? (
                <div className="text-center py-3 space-y-3" data-testid="onboarding-celebration">
                  <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <PartyPopper className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900"
                         style={{ fontFamily: "Manrope, sans-serif" }}>
                      Configuration terminée
                    </div>
                    <div className="text-xs text-slate-600 mt-1 leading-snug">
                      Ton IBAN, ton client, ta numérotation et ta 1re facture sont en place. Tu peux facturer sereinement 🚀
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={finishCelebration}
                    disabled={loading}
                    data-testid="onboarding-celebration-finish"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" />
                    Terminer
                  </Button>
                </div>
              ) : (
                <>
                  {steps.map((step) => (
                    <StepRow
                      key={step.id}
                      step={step}
                      isNext={nextStep?.id === step.id}
                      onGo={() => nav(step.cta_route)}
                    />
                  ))}
                  <div className="pt-2 flex items-center justify-between gap-2 border-t border-slate-100 mt-2">
                    <button
                      type="button"
                      onClick={dismiss}
                      disabled={loading}
                      className="text-[11px] text-slate-500 hover:text-slate-700 flex items-center gap-1 disabled:opacity-60"
                      data-testid="onboarding-checklist-dismiss">
                      {loading
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <X className="w-3 h-3" />}
                      Ne plus afficher
                    </button>
                    {nextStep && (
                      <Button
                        size="sm"
                        onClick={() => nav(nextStep.cta_route)}
                        data-testid="onboarding-checklist-next-cta"
                        className="h-7 px-2.5 text-xs bg-slate-900 hover:bg-slate-800 text-white gap-1">
                        <Sparkles className="w-3 h-3" />
                        {nextStep.cta_label}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function StepRow({ step, isNext, onGo }) {
  return (
    <motion.div
      layout
      initial={false}
      animate={step.done ? { scale: [1, 1.04, 1] } : {}}
      transition={{ duration: 0.35 }}
      className={cn(
        "flex items-start gap-2.5 rounded-lg p-2.5 transition",
        step.done ? "bg-emerald-50/40" : isNext ? "bg-violet-50/60 ring-1 ring-violet-200" : "hover:bg-slate-50",
      )}
      data-testid={`onboarding-step-${step.id}`}>
      {step.done
        ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
        : <Circle className={cn("w-4 h-4 mt-0.5 flex-shrink-0",
            isNext ? "text-violet-500" : "text-slate-300")} />}
      <div className="flex-1 min-w-0">
        <div className={cn("text-xs font-semibold",
              step.done ? "text-slate-500 line-through decoration-slate-300" : "text-slate-900")}>
          {step.title}
        </div>
        {!step.done && (
          <>
            <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">
              {step.description}
            </div>
            <button
              type="button"
              onClick={onGo}
              className="mt-1 text-[11px] font-medium text-violet-700 hover:text-violet-900 underline underline-offset-2"
              data-testid={`onboarding-step-${step.id}-cta`}>
              {step.cta_label} →
            </button>
          </>
        )}
      </div>
    </motion.div>
  );
}
